import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { PremiumReport, ReportDimensionKey } from "../../shared/types";
import { ReportView } from "../components/report/ReportView";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { getDevDemoPremiumReport } from "../lib/testApi";

type Tab = "v1" | "v1Mapping" | "internal" | "ideal" | "blueprint";

function isLocalHost() {
  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

function formatMs(ms: number) {
  const seconds = Math.round(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

export function DevReportPage() {
  const [report, setReport] = useState<PremiumReport | null>(null);
  const [tab, setTab] = useState<Tab>("v1");
  const [error, setError] = useState<string | null>(null);

  if (!isLocalHost()) {
    return (
      <main className="mx-auto w-full max-w-4xl px-4 py-20 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-white">404</h1>
        <p className="mt-3 text-slate-300">Η σελίδα δεν βρέθηκε.</p>
      </main>
    );
  }

  function loadDemoReport() {
    setError(null);
    getDevDemoPremiumReport()
      .then(setReport)
      .catch((err) => setError(err instanceof Error ? err.message : "Δεν φορτώθηκε το demo report."));
  }

  useEffect(() => {
    loadDemoReport();
  }, []);

  return (
    <main className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <section className="rounded-lg border border-amber-200/30 bg-amber-200/10 p-4 text-sm font-semibold text-amber-100">
        DEV ONLY — Creator report console. Δεν εμφανίζεται σε production.
      </section>

      <header className="mt-6">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-200">TryBrainTest Report Console</p>
        <h1 className="mt-2 text-3xl font-bold text-white sm:text-4xl">Premium report preview & mapping</h1>
        <p className="mt-3 max-w-3xl text-slate-300">
          Dev-only εργαλείο για να βλέπεις το user-facing report, την εσωτερική αλήθεια των raw κατηγοριών, το mapping και το roadmap επαγγελματικής εξέλιξης.
        </p>
      </header>

      <section className="glass-panel mt-6 rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-3">
          <DevTabButton active={tab === "v1"} onClick={() => setTab("v1")}>V1 Custom Report</DevTabButton>
          <DevTabButton active={tab === "v1Mapping"} onClick={() => setTab("v1Mapping")}>V1 Custom Report + Ανάλυση Συγχώνευσης</DevTabButton>
          <DevTabButton active={tab === "internal"} onClick={() => setTab("internal")}>Internal Mapping & Adjustments</DevTabButton>
          <DevTabButton active={tab === "ideal"} onClick={() => setTab("ideal")}>Ideal Future Report</DevTabButton>
          <DevTabButton active={tab === "blueprint"} onClick={() => setTab("blueprint")}>Blueprint</DevTabButton>
          <Button type="button" onClick={loadDemoReport} className="min-h-11">Generate V1 Demo Report</Button>
          <Button type="button" onClick={() => setTab("ideal")} variant="secondary" className="min-h-11">Show Ideal Future Report</Button>
          <Button type="button" onClick={() => window.print()} variant="secondary" className="min-h-11">Print / Save as PDF</Button>
        </div>
      </section>

      {error ? <p className="mt-6 rounded-lg border border-red-300/20 bg-red-300/10 p-4 text-red-100">{error}</p> : null}
      {!report ? <p className="mt-6 text-slate-300">Φορτώνουμε stable demo report...</p> : null}

      {report && tab === "v1" ? <ReportView report={report} /> : null}
      {report && tab === "v1Mapping" ? <V1ReportWithExpandableMapping report={report} /> : null}
      {report && tab === "internal" ? <InternalMappingAndAdjustments report={report} /> : null}
      {tab === "ideal" ? <IdealFutureReport /> : null}
      {tab === "blueprint" ? <ProfessionalBlueprint /> : null}
    </main>
  );
}

function DevTabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-11 rounded-lg border px-4 text-sm font-semibold ${
        active
          ? "border-cyan-200 bg-cyan-200/15 text-white"
          : "border-white/10 bg-white/[0.06] text-slate-200 hover:border-cyan-200/50"
      }`}
    >
      {children}
    </button>
  );
}

function LanguageToggle({ language, onChange }: { language: "en" | "el"; onChange: (language: "en" | "el") => void }) {
  return (
    <div className="inline-flex rounded-lg border border-white/10 bg-slate-950/50 p-1">
      <button
        type="button"
        onClick={() => onChange("en")}
        className={`rounded-md px-3 py-2 text-sm font-semibold ${language === "en" ? "bg-cyan-200 text-slate-950" : "text-slate-200 hover:bg-white/10"}`}
      >
        English
      </button>
      <button
        type="button"
        onClick={() => onChange("el")}
        className={`rounded-md px-3 py-2 text-sm font-semibold ${language === "el" ? "bg-cyan-200 text-slate-950" : "text-slate-200 hover:bg-white/10"}`}
      >
        Ελληνικά
      </button>
    </div>
  );
}

function V1ReportWithExpandableMapping({ report }: { report: PremiumReport }) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const mapping = report.internalRaw?.mapping ?? [];

  function toggle(dimension: ReportDimensionKey) {
    setExpanded((current) => ({ ...current, [dimension]: !current[dimension] }));
  }

  return (
    <section className="mt-6 rounded-2xl border border-white/10 bg-slate-50 text-slate-950 shadow-[0_30px_100px_rgba(15,23,42,0.35)]">
      <div className="rounded-t-2xl bg-gradient-to-br from-slate-950 via-indigo-950 to-cyan-950 p-6 text-white sm:p-8">
        <p className="inline-flex rounded-full border border-amber-200/30 bg-amber-200/10 px-3 py-1 text-xs font-bold text-amber-100">
          DEV ONLY — V1 Custom Report + Ανάλυση Συγχώνευσης
        </p>
        <h2 className="mt-4 text-4xl font-black">TryBrainTest Premium Report</h2>
        <p className="mt-3 max-w-3xl text-slate-200">
          Ίδια λογική παρουσίασης με το user-facing V1 report, αλλά οι διαστάσεις ανοίγουν για να δείχνουν το εσωτερικό mapping. Μην εμφανιστεί σε public/paid report.
        </p>
      </div>

      <div className="grid gap-5 p-6 sm:p-8 lg:grid-cols-[0.85fr_1.15fr]">
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <p className="text-sm font-bold text-indigo-700">IQ Score</p>
          <p className="mt-4 text-7xl font-black">{report.score.brainTestScore}</p>
          <p className="mt-2 text-lg font-bold text-slate-900">{report.score.scoreBand}</p>
          <p className="mt-3 text-sm text-slate-600">Range {report.score.estimateRange} · {report.score.percentileStyleLabel}</p>
          <p className="mt-4 rounded-xl bg-slate-100 p-4 text-sm leading-6 text-slate-600">{report.methodologyNote}</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <V1DevMetric label="Ακρίβεια" value={`${report.accuracy.accuracyPercent}%`} />
          <V1DevMetric label="Χρόνος" value={formatMs(report.totalTimeMs)} />
          <V1DevMetric label="Συνέπεια" value={report.responseQuality.consistencyLabel} />
        </div>
      </div>

      <div className="px-6 pb-8 sm:px-8">
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <h3 className="text-2xl font-black">Expandable report dimensions</h3>
          <p className="mt-2 text-sm text-slate-600">Collapsed cards match the public concept. Expanded content is creator-only and includes IDs/raw metadata.</p>
          <div className="mt-5 grid gap-4">
            {report.dimensions.map((dimension) => {
              const rows = mapping.filter((row) => row.dimension === dimension.dimension);
              const isOpen = Boolean(expanded[dimension.dimension]);
              return (
                <div key={dimension.dimension} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <button type="button" onClick={() => toggle(dimension.dimension)} className="flex w-full items-center justify-between gap-4 text-left">
                    <div>
                      <p className="font-black text-slate-950">{dimension.label}</p>
                      <p className="mt-1 text-sm leading-6 text-slate-600">{dimension.interpretation}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-black text-indigo-700">{dimension.scorePercent}%</p>
                      <p className="mt-1 text-xs font-bold uppercase tracking-wide text-slate-500">{isOpen ? "Collapse" : "Expand"}</p>
                    </div>
                  </button>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                    <div className="h-full rounded-full bg-gradient-to-r from-indigo-600 to-cyan-500" style={{ width: `${Math.max(0, Math.min(100, dimension.scorePercent))}%` }} />
                  </div>
                  {isOpen ? (
                    <div className="mt-4 rounded-xl border border-indigo-100 bg-white p-4">
                      <p className="text-sm font-bold text-indigo-800">Developer mapping detail</p>
                      <p className="mt-2 text-sm leading-6 text-slate-600">
                        V1 custom grouping: these active questions are merged into <strong>{dimension.label}</strong> to keep the paid report stable while all 30 questions still count.
                      </p>
                      <div className="mt-4 overflow-x-auto">
                        <table className="min-w-full text-left text-xs">
                          <thead className="text-slate-500">
                            <tr>
                              <th className="px-2 py-2">Slot</th>
                              <th className="px-2 py-2">Question ID</th>
                              <th className="px-2 py-2">Raw category / subtype</th>
                              <th className="px-2 py-2">Difficulty</th>
                              <th className="px-2 py-2">Mapping reason</th>
                              <th className="px-2 py-2">Contribution note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row) => (
                              <tr key={row.questionId} className="border-t border-slate-100">
                                <td className="px-2 py-2 font-mono">{row.slot}</td>
                                <td className="px-2 py-2 font-mono font-bold text-indigo-700">{row.questionId}</td>
                                <td className="px-2 py-2">{row.rawCategory} / {row.subtype}</td>
                                <td className="px-2 py-2">{row.difficulty}</td>
                                <td className="px-2 py-2">{row.reason}</td>
                                <td className="px-2 py-2">Counted in V1 public dimension score.</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="mt-3 text-xs leading-5 text-slate-500">
                        Future target: split this public dimension into more granular categories once the approved item bank has enough balanced coverage.
                      </p>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function V1DevMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
      <p className="text-sm font-semibold text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-black text-slate-950">{value}</p>
    </div>
  );
}

function InternalMappingAndAdjustments({ report }: { report: PremiumReport }) {
  const [language, setLanguage] = useState<"en" | "el">("en");
  return (
    <section className="mt-6 grid gap-6">
      <div className="flex justify-end">
        <LanguageToggle language={language} onChange={setLanguage} />
      </div>
      <InternalV1Explanation language={language} />
      <DimensionGroupingTable language={language} />
      <InternalRawReport report={report} />
      <MappingTable report={report} />
    </section>
  );
}

function InternalV1Explanation({ language }: { language: "en" | "el" }) {
  return (
    <Card>
      <h2 className="text-2xl font-bold text-white">Internal V1 Custom Report Explanation</h2>
      {language === "en" ? (
        <div className="mt-4 grid gap-3 text-sm leading-6 text-slate-300">
          <p>The current v1 user report is intentionally aggregated into broader report dimensions.</p>
          <p>This prevents showing unstable or misleading raw counts like <strong className="text-white">0/1 verbal</strong> or <strong className="text-white">5/13 matrix</strong>.</p>
          <p>All 30 questions are still counted. No question is ignored.</p>
          <p>Raw categories and subtypes are mapped into 5 broader user-facing dimensions for the commercial v1 report strategy.</p>
          <p>The terminology stays useful for internal review: fixed-form test, item bank, category mapping, raw categories, calibration, benchmark, validity, reliability, and item metadata.</p>
          <p>As the bank grows and becomes balanced, the report can become more granular and gradually move toward direct category reporting.</p>
        </div>
      ) : (
        <div className="mt-4 grid gap-3 text-sm leading-6 text-slate-300">
          <p>Το τρέχον V1 user-facing report συγχωνεύει σκόπιμα τις raw κατηγορίες σε ευρύτερες διαστάσεις.</p>
          <p>Έτσι δεν εμφανίζονται ασταθή ή παραπλανητικά raw counts όπως <strong className="text-white">0/1 verbal</strong> ή <strong className="text-white">5/13 matrix</strong>.</p>
          <p>Και οι 30 ερωτήσεις μετράνε. Καμία ερώτηση δεν αγνοείται.</p>
          <p>Οι raw κατηγορίες και τα subtypes χαρτογραφούνται σε 5 broader user-facing dimensions για το custom εμπορικό V1 report.</p>
          <p>Όσο μεγαλώνει και ισορροπεί το bank, το report μπορεί να γίνει πιο granular και να πλησιάσει direct category reporting.</p>
        </div>
      )}
    </Card>
  );
}

function DimensionGroupingTable({ language }: { language: "en" | "el" }) {
  const rows = [
    {
      dimension: "Οπτική Λογική & Μοτίβα",
      includes: "matrix patterns, missing shapes, cyclic patterns, symbol/color/shape matrices, visual rule discovery",
      why: "Combines visually similar rule-discovery tasks so the public report reads as a stable cognitive dimension.",
      future: "Split into Pattern/Matrix and Multi-rule Visual Reasoning when each has enough approved items."
    },
    {
      dimension: "Χωρική & Δομική Αντίληψη",
      includes: "rotations, top-view tasks, figure completion, spatial shape logic, structural transformations",
      why: "Groups spatial construction and transformation tasks under one professional spatial dimension.",
      future: "Separate mental rotation, 3D/top-view, and figure completion subscales."
    },
    {
      dimension: "Συμβολική & Ποσοτική Λογική",
      includes: "symbol equations, numeric/symbolic sequences, alphanumeric rules, arithmetic deduction",
      why: "Avoids fragmenting number/symbol items while preserving their quantitative reasoning signal.",
      future: "Separate Symbolic Logic and Numerical Sequencing after expansion."
    },
    {
      dimension: "Εξαγωγή Κανόνων & Συλλογισμός",
      includes: "XOR/union logic, comparison rules, multi-rule deduction, rule-based sequence logic",
      why: "Captures controlled rule extraction across matrix and logic item families.",
      future: "Split deduction, conditional reasoning, and abstract logic once balanced."
    },
    {
      dimension: "Ταχύτητα & Προσοχή",
      includes: "visual search, quick-count, wrong-tile selection, attention/scanning tasks",
      why: "Keeps speed and attention tasks interpretable without exposing raw slot distribution.",
      future: "Separate processing speed and selective attention if enough items exist."
    }
  ];

  return (
    <Card>
      <h2 className="text-2xl font-bold text-white">Public Dimension Grouping Strategy</h2>
      {language === "el" ? (
        <p className="mt-2 text-sm leading-6 text-slate-300">
          Ο πίνακας εξηγεί ποιες raw κατηγορίες και subtypes συγχωνεύονται, γιατί υπάρχει αυτή η συγχώνευση στο V1 report και ποιος είναι ο μελλοντικός στόχος.
        </p>
      ) : (
        <p className="mt-2 text-sm leading-6 text-slate-300">
          This table explains what is merged, why the v1 grouping exists, and how the future calibrated report can become more granular.
        </p>
      )}
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full border-separate border-spacing-y-2 text-left text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-3 py-2">Public report dimension</th>
              <th className="px-3 py-2">Included raw categories/subtypes</th>
              <th className="px-3 py-2">Why this grouping exists</th>
              <th className="px-3 py-2">Future target</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.dimension} className="bg-slate-950/50 text-slate-200">
                <td className="rounded-l-lg px-3 py-3 font-semibold text-white">{row.dimension}</td>
                <td className="px-3 py-3">{row.includes}</td>
                <td className="px-3 py-3">{row.why}</td>
                <td className="rounded-r-lg px-3 py-3">{row.future}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function InternalRawReport({ report }: { report: PremiumReport }) {
  return (
    <section className="mt-6 grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
      <Card>
        <h2 className="text-2xl font-bold text-white">Raw category counts & accuracy</h2>
        <div className="mt-4 grid gap-3">
          {report.internalRaw?.categoryBreakdown.map((category) => (
            <div key={category.category} className="rounded-lg border border-white/10 bg-white/[0.045] p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-white">{category.label}</p>
                  <p className="text-xs text-slate-400">{category.category}</p>
                </div>
                <p className="font-mono text-sm text-cyan-100">{category.correctCount}/{category.questionCount} · {category.weightedScorePercent}%</p>
              </div>
              <p className="mt-2 text-xs text-slate-400">IDs: {category.questionIds.join(", ")}</p>
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <h2 className="text-2xl font-bold text-white">Scoring components</h2>
        <dl className="mt-4 grid gap-3 text-sm">
          <RawRow label="Accuracy + difficulty" value={`${report.internalRaw?.scoringComponents.accuracyDifficulty ?? 0}%`} />
          <RawRow label="Time performance" value={`${report.internalRaw?.scoringComponents.timePerformance ?? 0}%`} />
          <RawRow label="Response quality" value={`${report.internalRaw?.scoringComponents.responseQuality ?? 0}%`} />
          <RawRow label="Combined" value={`${report.internalRaw?.scoringComponents.combined ?? 0}%`} />
          <RawRow label="Raw accuracy" value={`${report.accuracy.correctCount}/${report.totalQuestions}`} />
          <RawRow label="Total time" value={formatMs(report.totalTimeMs)} />
          <RawRow label="Hard correct" value={String(report.difficulty.hardCorrectCount)} />
          <RawRow label="Expert correct" value={String(report.difficulty.expertCorrectCount)} />
        </dl>
        {report.internalRaw?.interpretationDebug ? (
          <>
            <h3 className="mt-6 text-lg font-bold text-white">Interpretation / recommendation rules</h3>
            <dl className="mt-3 grid gap-3 text-sm">
              <RawRow label="Score band rule" value={report.internalRaw.interpretationDebug.scoreBandRule} />
              <RawRow label="Strongest dimension" value={report.internalRaw.interpretationDebug.strongestDimension} />
              <RawRow label="Weakest dimension" value={report.internalRaw.interpretationDebug.weakestDimension} />
              <RawRow label="Speed profile" value={report.internalRaw.interpretationDebug.speedProfile} />
              <RawRow label="Quality rule" value={report.internalRaw.interpretationDebug.qualityRule} />
              <RawRow
                label="Recommendation rules"
                value={report.internalRaw.interpretationDebug.recommendationRules.join(", ")}
              />
            </dl>
          </>
        ) : null}
        <h3 className="mt-6 text-lg font-bold text-white">Difficulty breakdown</h3>
        <pre className="mt-3 overflow-auto rounded-lg border border-white/10 bg-slate-950/60 p-3 text-xs text-slate-200">
          {JSON.stringify(report.activeFormAnalysis.difficultyCounts, null, 2)}
        </pre>
      </Card>
    </section>
  );
}

function MappingTable({ report }: { report: PremiumReport }) {
  const mapping = report.internalRaw?.mapping ?? [];
  return (
    <section className="mt-6 rounded-lg border border-white/10 bg-white/[0.045] p-4">
      <h2 className="text-2xl font-bold text-white">Public Dimension Mapping</h2>
      <p className="mt-2 text-sm text-slate-300">Όλες οι ερωτήσεις μετράνε. Η user-facing αναφορά εμφανίζει μόνο τις ευρύτερες διαστάσεις.</p>
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full border-separate border-spacing-y-2 text-left text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-3 py-2">Slot</th>
              <th className="px-3 py-2">Question ID</th>
              <th className="px-3 py-2">Raw category</th>
              <th className="px-3 py-2">Subtype</th>
              <th className="px-3 py-2">Difficulty</th>
              <th className="px-3 py-2">Public dimension</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2">Counted</th>
            </tr>
          </thead>
          <tbody>
            {mapping.map((row) => (
              <tr key={row.questionId} className="bg-slate-950/50 text-slate-200">
                <td className="rounded-l-lg px-3 py-3 font-mono">{row.slot}</td>
                <td className="px-3 py-3 font-mono text-cyan-100">{row.questionId}</td>
                <td className="px-3 py-3">{row.rawCategoryLabel}</td>
                <td className="px-3 py-3 font-mono text-xs">{row.subtype}</td>
                <td className="px-3 py-3">{row.difficulty}</td>
                <td className="px-3 py-3 font-semibold text-white">{row.dimensionLabel}</td>
                <td className="px-3 py-3 text-slate-300">{row.reason}</td>
                <td className="rounded-r-lg px-3 py-3">{row.countedInPublicReport ? "yes" : "no"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function IdealFutureReport() {
  const categories = [
    ["Μοτίβα & Matrix", "88%", "7/8", "Top 10%", "Υψηλή", "Πολύ καλή εξαγωγή κανόνων σε ισορροπημένα matrix items."],
    ["Χωρική Αντίληψη", "75%", "3/4", "Top 25%", "Μεσαία-υψηλή", "Σταθερή χωρική οπτικοποίηση και δομική αναγνώριση."],
    ["Συμβολική & Ποσοτική Λογική", "80%", "4/5", "Top 18%", "Υψηλή", "Καθαρός χειρισμός συμβόλων, αριθμών και ακολουθιών."],
    ["Λογική Συμπερασμάτων", "75%", "3/4", "Top 25%", "Μεσαία-υψηλή", "Καλή επίδοση σε κανόνες, αποκλεισμό και συλλογισμό."],
    ["Λεκτική Λογική", "80%", "4/5", "Top 20%", "Μεσαία-υψηλή", "Σταθερή επίδοση σε αναλογίες και εννοιολογικές σχέσεις."],
    ["Ταχύτητα & Προσοχή", "75%", "3/4", "Top 25%", "Μεσαία-υψηλή", "Αποτελεσματική σάρωση με ελεγχόμενο ρυθμό απόκρισης."]
  ];

  return (
    <section className="mt-6 rounded-2xl border border-amber-200/25 bg-slate-50 text-slate-950 shadow-[0_30px_100px_rgba(15,23,42,0.35)]">
      <div className="rounded-t-2xl bg-gradient-to-br from-slate-950 via-indigo-950 to-cyan-950 p-6 text-white sm:p-8">
        <p className="inline-flex rounded-full border border-amber-200/30 bg-amber-200/10 px-3 py-1 text-xs font-bold text-amber-100">
          DEV ONLY — θεωρητικό reference, όχι τρέχον live προϊόν.
        </p>
        <h2 className="mt-4 text-4xl font-black">TryBrainTest — Θεωρητικό Future Report</h2>
        <p className="mt-3 max-w-3xl text-slate-200">Developer-only reference για το μελλοντικό balanced report system.</p>
        <p className="mt-3 max-w-3xl rounded-lg border border-white/10 bg-white/10 p-3 text-sm leading-6 text-slate-100">
          Αυτό το report είναι θεωρητικό reference για τον developer. Δεν είναι το τρέχον live report και δεν επηρεάζει το V1 Custom Report.
        </p>
      </div>

      <div className="grid gap-5 p-6 sm:p-8 lg:grid-cols-[0.85fr_1.15fr]">
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <p className="text-sm font-bold text-indigo-700">Μελλοντικό balanced model</p>
          <p className="mt-4 text-7xl font-black">127</p>
          <p className="mt-2 text-xl font-bold text-slate-900">IQ Score</p>
          <dl className="mt-5 grid gap-3 text-sm">
            <FutureRow label="Estimated Range" value="121–132" />
            <FutureRow label="Position" value="Top 12%" />
            <FutureRow label="Confidence" value="Υψηλή" />
            <FutureRow label="Report maturity" value="Μελλοντικό balanced model" />
            <FutureRow label="User test length" value="30 questions per attempt" />
            <FutureRow label="Synthetic accuracy" value="24/30 · 80%" />
          </dl>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <FutureCard title="Speed vs Accuracy" body="Μελλοντική κάρτα/γράφημα που συγκρίνει ρυθμό απόκρισης και σταθμισμένη ακρίβεια ανά δυσκολία." />
          <FutureCard title="Ανάλυση δυσκολίας" body="Easy / Medium / Hard / Expert επίδοση με calibrated item difficulty." />
          <FutureCard title="Reliability & Confidence" body="Εσωτερική εκτίμηση αξιοπιστίας, confidence interval και κάλυψης υποκλιμάκων." />
          <FutureCard title="Retest comparison" body="Placeholder για μεταβολή score, βελτίωση διαστάσεων και συνέπεια ανά attempt." />
          <FutureCard title="Norm calibration" body="Placeholder για benchmark sample, age-band reference και percentile-style model." />
          <FutureCard title="Adaptive readiness" body="Ένδειξη για το αν το bank είναι αρκετά μεγάλο/calibrated για adaptive ή semi-adaptive επιλογή." />
        </div>
      </div>

      <div className="px-6 pb-6 sm:px-8">
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <h3 className="text-2xl font-black">Μελλοντικές balanced κατηγορίες 30-question attempt</h3>
          <div className="mt-5 overflow-x-auto">
            <table className="min-w-full border-separate border-spacing-y-2 text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Κατηγορία</th>
                  <th className="px-3 py-2">Score</th>
                  <th className="px-3 py-2">Σωστά/Σύνολο</th>
                  <th className="px-3 py-2">Position</th>
                  <th className="px-3 py-2">Confidence</th>
                  <th className="px-3 py-2">Ερμηνεία</th>
                </tr>
              </thead>
              <tbody>
                {categories.map(([name, score, correct, position, confidence, interpretation]) => (
                  <tr key={name} className="bg-slate-50">
                    <td className="rounded-l-lg px-3 py-3 font-bold">{name}</td>
                    <td className="px-3 py-3 font-black text-indigo-700">{score}</td>
                    <td className="px-3 py-3">{correct}</td>
                    <td className="px-3 py-3">{position}</td>
                    <td className="px-3 py-3">{confidence}</td>
                    <td className="rounded-r-lg px-3 py-3 text-slate-600">{interpretation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-5 rounded-2xl bg-indigo-50 p-6 ring-1 ring-indigo-100">
          <h3 className="text-xl font-black">Test length vs future bank target</h3>
          <p className="mt-3 text-sm leading-7 text-slate-700">
            Το final user test μπορεί να παραμείνει 30 ερωτήσεις. Το μεγάλο bank είναι για επιλογή, ποικιλία, calibration και retesting.
            Ο στόχος bank είναι 150–200+ approved questions, ώστε ο χρήστης να μη βλέπει 150+ ερωτήσεις αλλά το σύστημα να έχει καλύτερη ισορροπία, rotation, retests και μελλοντική semi-adaptive/adaptive επιλογή.
          </p>
        </div>

        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <h3 className="text-xl font-black">Μελλοντικές συστάσεις</h3>
            <ul className="mt-4 grid gap-2 text-sm leading-6 text-slate-700">
              <li>Επέκταση calibrated matrix και spatial pools πριν από granular public subscale claims.</li>
              <li>Δημιουργία equivalent forms για σύγκριση retest.</li>
              <li>Confidence και reliability bands μόνο μετά από αρκετά benchmark attempts.</li>
              <li>Η public γλώσσα να παραμένει performance-focused ακόμη και στο mature model.</li>
            </ul>
          </div>
          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <h3 className="text-xl font-black">Μελλοντικές methodology notes</h3>
            <p className="mt-4 text-sm leading-7 text-slate-700">
              Αυτό το ideal reference προϋποθέτει balanced 30-question attempt, μεγαλύτερο calibrated bank, internal benchmark data, reliability estimates και προσεκτικά scoped percentile-style reporting. Δεν συνδέεται με live scoring και δεν αντιπροσωπεύει το τρέχον paid V1 report.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function FutureCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
      <p className="font-black text-slate-950">{title}</p>
      <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
    </div>
  );
}

function FutureRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-2 last:border-b-0">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-bold">{value}</dd>
    </div>
  );
}

function AdjustmentNotes() {
  return (
    <Card className="mt-6">
      <h2 className="text-2xl font-bold text-white">Report Adjustment Notes</h2>
      <div className="mt-4 grid gap-3 text-sm leading-6 text-slate-300">
        <p>Η public αναφορά χρησιμοποιεί ευρύτερες report dimensions ώστε να μην εμφανίζει παραπλανητικά raw counts όπως “0/1 verbal” ή “5/13 matrix”.</p>
        <p>Καμία ερώτηση δεν αγνοείται. Όλες οι 30 ερωτήσεις συμμετέχουν στο scoring και στο mapping.</p>
        <p>Οι raw κατηγορίες συγχωνεύονται σε επαγγελματικές διαστάσεις που ταιριάζουν καλύτερα σε commercial cognitive-performance report v1.</p>
        <p>Αυτό είναι στρατηγική v1 για το τρέχον fixed form. Μελλοντική επέκταση και καλύτερη ισορροπία bank θα επιτρέψει πιο granular δημόσιες υποκλίμακες.</p>
      </div>
    </Card>
  );
}

function ProfessionalBlueprint() {
  const [language, setLanguage] = useState<"en" | "el">("en");
  const sections = [
    ["Current Status", "Το τρέχον test υποστηρίζει polished commercial cognitive-performance report, όχι επίσημο/κλινικά calibrated IQ claim."],
    ["Why not fully psychometric yet", "Δεν υπάρχει norm sample, item calibration, reliability analysis ή true percentile reference population."],
    ["What the report can do now", "Να παρουσιάζει reasoning profile, fixed-form performance, timing, consistency, strengths και practical recommendations."],
    ["Correct 30-question balance blueprint", "Pattern/Matrix 8, Spatial 4, Symbol/Number 5, Logical Deduction 4, Verbal 5, Processing Speed/Attention 4."],
    ["Category details", "Κάθε κατηγορία πρέπει να έχει αρκετά items ώστε δημόσια υποκλίμακα να μην βασίζεται σε ένα μόνο question."],
    ["Difficulty balance blueprint", "Σταδιακή κλιμάκωση easy → medium → hard → expert, με αποφυγή υπερβολικού front-loading δύσκολων items."],
    ["Required metadata", "Κάθε question χρειάζεται category, subtype, difficulty, estimated/max time, renderer, scoring notes, explanation και quality status."],
    ["Approval criteria", "Μοναδική σωστή απάντηση, καθαρό visual, mobile readability, χαμηλό ambiguity risk, passing validation/audit."],
    ["Report data use", "Το report πρέπει να χρησιμοποιεί accuracy, difficulty, timing, response quality και dimension mapping, όχι client-calculated score."],
    ["Safer report language", "Χρησιμοποιούμε Brain Test Score, performance range και reasoning profile. Αποφεύγουμε official IQ ή clinical claims."],
    ["Why fixed-form", "Το fixed form είναι αποδεκτό v1 γιατί κρατά σταθερό περιεχόμενο, συγκρίσιμη εμπειρία και απλούστερο report logic."],
    ["Future adaptive needs", "Μεγάλο calibrated bank, exposure controls, item difficulty estimates, discrimination stats και dynamic selection."],
    ["Adaptive stages", "1. fixed form, 2. multiple equivalent forms, 3. difficulty-branched sections, 4. calibrated adaptive testing."],
    ["Creator checklist: questions", "Originality, clean rule, exact answer, strong distractors, renderer QA, mobile QA, dev explanation."],
    ["Creator checklist: report", "No raw imbalance, no overclaiming, all items counted, clear dimensions, print-ready layout, secure paid unlock later."],
    ["Final internal rule", "Public report should feel premium and honest: useful performance insight without pretending to be a certified psychometric IQ instrument."]
  ];
  const greekSections = [
    ["Current Status", "Το τρέχον test υποστηρίζει polished commercial cognitive-performance report, όχι επίσημο ή κλινικά calibrated IQ claim."],
    ["Why a fully professional psychometric report cannot be claimed yet", "Δεν υπάρχει ακόμη norm sample, item calibration, reliability analysis ή πραγματικό reference population για true percentile."],
    ["What the report can professionally do right now", "Μπορεί να παρουσιάζει reasoning profile, επίδοση στο 30-question attempt, timing, consistency, strengths και πρακτικές συστάσεις."],
    ["Correct 30-question balance blueprint", "Μοτίβα/Matrix 8, Χωρική 4, Symbol/Number 5, Logical Deduction 4, Verbal 5, Processing Speed/Attention 4."],
    ["Category details", "Κάθε κατηγορία χρειάζεται αρκετά approved items ώστε μια public υποκλίμακα να μη βασίζεται σε πολύ μικρό δείγμα."],
    ["Difficulty balance blueprint", "Σταδιακή κλιμάκωση easy → medium → hard → expert χωρίς υπερβολικό front-loading δύσκολων items."],
    ["Required metadata for every question", "Κάθε item χρειάζεται category, subtype, difficulty, estimated/max time, renderer, scoring notes, explanation και quality status."],
    ["Approval criteria", "Μοναδική σωστή απάντηση, καθαρό visual, mobile readability, χαμηλό ambiguity risk και passing validation/audit."],
    ["How the report should use question data", "Το report πρέπει να χρησιμοποιεί accuracy, difficulty, timing, response quality και dimension mapping, ποτέ client-calculated score."],
    ["Safer report language", "Χρησιμοποιούμε IQ Score, performance range και reasoning profile. Αποφεύγουμε official IQ ή clinical claims."],
    ["Why the current test is fixed-form, not adaptive", "Το fixed-form είναι σωστό για V1 γιατί κρατά σταθερό περιεχόμενο, συγκρίσιμη εμπειρία και απλούστερο report logic."],
    ["What is needed for future adaptive testing", "Μεγάλο calibrated bank, exposure controls, item difficulty estimates, discrimination stats και dynamic selection."],
    ["Adaptive development stages", "1. fixed form, 2. multiple equivalent forms, 3. difficulty-branched sections, 4. calibrated adaptive testing."],
    ["Creator checklist for new questions", "Originality, clean rule, exact answer, strong distractors, renderer QA, mobile QA και dev explanation."],
    ["Creator checklist for report professionalism", "No raw imbalance in public report, no overclaiming, all items counted, clear dimensions, print-ready layout, secure paid unlock later."],
    ["Final internal rule", "Το public report πρέπει να φαίνεται premium και έντιμο: χρήσιμο performance insight χωρίς να προσποιείται certified psychometric IQ instrument."]
  ];
  const visibleSections = language === "en" ? sections : greekSections;

  return (
    <Card className="mt-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold text-white">Professional Test & Report Blueprint</h2>
        <LanguageToggle language={language} onChange={setLanguage} />
      </div>
      <p className="mt-3 rounded-lg border border-cyan-200/15 bg-cyan-200/[0.055] p-3 text-sm leading-6 text-slate-300">
        User test length can remain 30 questions per attempt. The future bank target is 150–200+ approved questions for selection, variety, calibration, retesting, and future semi-adaptive/adaptive routing.
      </p>
      <div className="mt-5 grid gap-3 md:grid-cols-2">
        {visibleSections.map(([title, body]) => (
          <div key={title} className="rounded-lg border border-white/10 bg-white/[0.045] p-4">
            <h3 className="font-bold text-cyan-100">{title}</h3>
            <p className="mt-2 text-sm leading-6 text-slate-300">{body}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

function RawRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-white/10 bg-white/[0.04] p-3">
      <dt className="text-slate-400">{label}</dt>
      <dd className="font-semibold text-white">{value}</dd>
    </div>
  );
}
